import React, { useEffect, useState } from "react";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { api, clearAuth, getProfile } from "@/lib/api";
import { LayoutDashboard, Newspaper, Award, FileCode, Settings, ClipboardList, LogOut, ExternalLink, CalendarClock } from "lucide-react";
import { CohnectLogo } from "@/components/site/CohnectLogo";

const NAV_ALL = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/issues", label: "Issues", icon: Newspaper },
  { to: "/admin/automation", label: "Automation", icon: CalendarClock },
  { to: "/admin/sponsors", label: "Sponsors", icon: Award },
];
const NAV_OWNER = [
  { to: "/admin/prompts", label: "Prompts", icon: FileCode },
  { to: "/admin/settings", label: "Settings", icon: Settings },
  { to: "/admin/audit-log", label: "Audit log", icon: ClipboardList },
];

export const AdminLayout = ({ children }) => {
  const nav = useNavigate();
  const [profile, setProfile] = useState(getProfile());

  useEffect(() => {
    api.get("/admin/me").then((r) => {
      setProfile(r.data);
    }).catch(() => {
      clearAuth();
      nav("/admin/login", { replace: true });
    });
  }, [nav]);

  const items = [...NAV_ALL, ...(profile?.role === "owner" ? NAV_OWNER : [])];

  const logout = () => {
    clearAuth();
    nav("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[color:var(--coh-paper)]">
      <div className="flex">
        <aside className="hidden md:flex md:w-60 lg:w-64 flex-col border-r border-border min-h-screen bg-card">
          <div className="px-5 py-5 border-b border-border">
            <CohnectLogo iconSize={20} />
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">Admin</div>
          </div>
          <nav className="flex-1 p-3 space-y-1 text-sm">
            {items.map((it) => {
              const Icon = it.icon;
              return (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  data-testid={`admin-nav-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${isActive ? "bg-[color:var(--coh-blue)]/10 text-[color:var(--coh-blue)] font-semibold" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`
                  }
                >
                  <Icon className="h-4 w-4" />
                  <span>{it.label}</span>
                </NavLink>
              );
            })}
          </nav>
          <div className="p-3 border-t border-border">
            <div className="px-2 mb-2 text-xs text-muted-foreground">
              <div className="truncate text-foreground">{profile?.email}</div>
              <div className="font-mono uppercase text-[10px] text-[color:var(--coh-blue)]">{profile?.role}</div>
            </div>
            <button onClick={logout} data-testid="admin-logout-button" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-secondary hover:text-foreground">
              <LogOut className="h-4 w-4" /> Sign out
            </button>
            <Link to="/" target="_blank" className="mt-2 w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-muted-foreground hover:bg-secondary hover:text-foreground">
              <ExternalLink className="h-4 w-4" /> View public site
            </Link>
          </div>
        </aside>
        <main className="flex-1 min-h-screen">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6 sm:py-10">{children}</div>
        </main>
      </div>
    </div>
  );
};
